import { Combobox } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  EmptyState,
  SimpleErrorNotification,
  SimpleLoader,
  ViewSubSection,
} from "shared/components";
import { useBackOfficeTags } from "./useBackOfficeTags";
import { OperatorByIdQuery } from "gql/generated";

type BackOfficeTagsProps = {
  operator: OperatorByIdQuery["operatorById"];
  operatorEntityId: string | undefined;
  organizationId: string | undefined;
};

export const BackOfficeTags = ({
  organizationId,
  operator,
  operatorEntityId,
}: BackOfficeTagsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    errorTags,
    filterableBackOfficeTags,
    loadingTags,
    onComboboxOpenChange,
    onTagInputValueSearch,
    onUpdateSelectedTagIds,
    selectedBackOfficeTagIds,
  } = useBackOfficeTags(operator, operatorEntityId, organizationId);
  return (
    <ViewSubSection
      title={t("directory:OPERATOR.BACK_OFFICE.TAGS.TITLE")}
      description={
        filterableBackOfficeTags.length > 0
          ? t("directory:OPERATOR.BACK_OFFICE.TAGS.DESCRIPTION")
          : undefined
      }
    >
      {filterableBackOfficeTags.length > 0 && !loadingTags ? (
        <Combobox.Root
          value={selectedBackOfficeTagIds}
          onValueChange={(d) => onUpdateSelectedTagIds(d.value)}
          onInputValueChange={(d) => onTagInputValueSearch(d.inputValue)}
          onOpenChange={onComboboxOpenChange}
          items={filterableBackOfficeTags}
          placeholder={`${t("directory:OPERATOR.BACK_OFFICE.TAGS.PLACEHOLDER_LABEL")}...`}
          css={{
            "& [data-scope='combobox'][data-part='input'], [data-scope='combobox'][data-part='input-wrapper']":
              {
                bg: "white",
              },
          }}
          multiple
        >
          {filterableBackOfficeTags.map((item) => (
            <Combobox.Item key={item.value} item={item} {...item} />
          ))}
        </Combobox.Root>
      ) : (
        <>
          {errorTags ? (
            <SimpleErrorNotification />
          ) : (
            <>
              {filterableBackOfficeTags.length < 1 && !loadingTags ? (
                <EmptyState
                  text={t("directory:OPERATOR.BACK_OFFICE.TAGS.EMPTY")}
                  ctaButtonProps={{
                    children: t(
                      "directory:OPERATOR.BACK_OFFICE.TAGS.EMPTY_CTA",
                    ),
                    size: "md",
                    onClick: () =>
                      navigate(`/app/organization/${organizationId}/profile`),
                  }}
                />
              ) : (
                <SimpleLoader />
              )}
            </>
          )}
        </>
      )}
    </ViewSubSection>
  );
};
